import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTr } from "@/autoTranslate";

const links = [
  { id: "intro", label: "Giriş" },
  { id: "manifesto", label: "Manifesto" },
  { id: "works", label: "İşler" },
  { id: "pour", label: "Akış" },
  { id: "projects", label: "Projeler" },
  { id: "grid-match", label: "Eşleştir" },
  { id: "skills", label: "Zanaat" },
  { id: "contact", label: "İletişim" },
];

function scrollTo(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function Navigation() {
  const tr = useTr();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-40 px-6 md:pl-10 md:pr-[140px] py-4 flex items-center gap-6 lg:gap-8 transition-[background-color,backdrop-filter,border-color] duration-300 ${
          scrolled
            ? "bg-[color:var(--paper)]/95 backdrop-blur-md border-b border-[color:var(--hair)] text-[color:var(--ink)]"
            : "text-[color:var(--ink)]"
        }`}
      >
        <button
          data-testid="nav-menu-btn"
          className="md:hidden font-mono-label text-[11px] uppercase tracking-widest border border-current rounded-full px-3 py-2"
          onClick={() => setOpen(true)}
        >
          {tr("Menü +")}
        </button>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8 font-mono-label text-[10px] lg:text-[11px] uppercase tracking-widest">
          {links.map((l, i) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={(e) => {
                e.preventDefault();
                scrollTo(l.id);
              }}
              data-testid={`nav-link-${l.id}`}
              data-cursor="hover"
              data-cursor-label="go"
              className="u-link"
            >
              {String(i).padStart(2, "0")} / {tr(l.label)}
            </a>
          ))}
        </nav>

        <div className="flex-1" aria-hidden />
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.65, ease: [0.85, 0, 0.15, 1] }}
            className="fixed inset-0 z-50 bg-[var(--ink)] text-[var(--bone)] p-8 flex flex-col justify-between"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono-label text-xs uppercase tracking-widest">
                {tr("Dizin")}
              </span>
              <button
                onClick={() => setOpen(false)}
                className="font-mono-label text-xs uppercase tracking-widest border border-current rounded-full px-3 py-2"
                data-testid="mobile-menu-close"
              >
                {tr("Kapat ×")}
              </button>
            </div>
            <nav className="flex flex-col gap-4">
              {links.map((l, i) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setOpen(false);
                    setTimeout(() => scrollTo(l.id), 500);
                  }}
                  className="font-serif-editorial italic text-4xl"
                  data-testid={`mobile-nav-link-${l.id}`}
                >
                  <span className="font-mono-label not-italic text-sm opacity-60 mr-3">
                    {String(i).padStart(2, "0")}
                  </span>
                  {tr(l.label)}
                </a>
              ))}
            </nav>
            <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
